import React, { useState, useEffect } from "react";
import ApiService from "../services/ApiService";
import "./UserManagement.css";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [residencies, setResidencies] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [flats, setFlats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [communityData, setCommunityData] = useState({
    residencyId: "",
    blockId: "",
    flatId: "",
  });
  const [editData, setEditData] = useState({
    mobileNumber: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterResidency, setFilterResidency] = useState("");

  useEffect(() => {
    loadUsers();
    loadResidencies();
  }, []);
  useEffect(() => {
    if (communityData.residencyId && !isEditing) {
      loadBlocks(communityData.residencyId);
    }
  }, [communityData.residencyId, isEditing]);

  useEffect(() => {
    if (communityData.blockId && !isEditing) {
      loadFlats(communityData.blockId);
    }
  }, [communityData.blockId, isEditing]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAllUsers();
      setUsers(response.data || []);
    } catch (error) {
      console.error("Error loading users:", error);
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const loadResidencies = async () => {
    try {
      const response = await ApiService.getResidencies();
      setResidencies(response.data || []);
    } catch (error) {
      console.error("Error loading residencies:", error);
    }
  };
  const loadBlocks = async (residencyId) => {
    try {
      const response = await ApiService.getBlocks(residencyId);
      setBlocks(response.data || []);
      setCommunityData((prev) => ({ ...prev, blockId: "", flatId: "" }));
    } catch (error) {
      console.error("Error loading blocks:", error);
    }
  };

  const loadFlats = async (blockId) => {
    try {
      const response = await ApiService.getFlats(blockId);
      setFlats(response.data || []);
      setCommunityData((prev) => ({ ...prev, flatId: "" }));
    } catch (error) {
      console.error("Error loading flats:", error);
    }
  };

  // Separate functions for loading data during editing (without resetting state)
  const loadBlocksForEditing = async (residencyId) => {
    try {
      const response = await ApiService.getBlocks(residencyId);
      setBlocks(response.data || []);
    } catch (error) {
      console.error("Error loading blocks for editing:", error);
    }
  };

  const loadFlatsForEditing = async (blockId) => {
    try {
      const response = await ApiService.getFlats(blockId);
      setFlats(response.data || []);
    } catch (error) {
      console.error("Error loading flats for editing:", error);
    }
  };
  const handleEditUser = async (user) => {
    setIsEditing(true); // Prevent useEffect hooks from interfering
    setEditingUser(user);
    setCommunityData({
      residencyId: user.residencyId || "",
      blockId: user.blockId || "",
      flatId: user.flatId || "",
    });
    setEditData({
      mobileNumber: user.mobileNumber || "",
    });
    setError("");
    setMessage("");

    // Load blocks if residency is set
    if (user.residencyId) {
      try {
        await loadBlocksForEditing(user.residencyId);

        // Load flats if block is also set
        if (user.blockId) {
          await loadFlatsForEditing(user.blockId);
        }
      } catch (error) {
        console.error("Error loading community data for editing:", error);
      }
    }
  };
  const handleCommunityChange = (e) => {
    const { name, value } = e.target;
    setCommunityData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Re-enable useEffect hooks for cascade behavior when user manually changes dropdowns
    if (name === "residencyId" || name === "blockId") {
      setIsEditing(false);
    }
  };

  const handleEditDataChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleSaveCommunity = async () => {
    if (!editingUser) return;

    if (
      !communityData.residencyId ||
      !communityData.blockId ||
      !communityData.flatId
    ) {
      setError("Please select residency, block, and flat");
      return;
    }

    // Validate mobile number if provided
    if (
      editData.mobileNumber &&
      !/^[6-9]\d{9}$/.test(editData.mobileNumber.trim())
    ) {
      setError("Please enter a valid 10-digit mobile number starting with 6-9");
      return;
    }

    try {
      setLoading(true);

      // Update community assignment
      await ApiService.updateUserCommunity(editingUser.id, communityData);

      // Update phone number if changed
      if (editData.mobileNumber !== editingUser.mobileNumber) {
        await ApiService.updateUserProfile(editingUser.firebaseUid, {
          mobileNumber: editData.mobileNumber.trim(),
        });
      }
      setMessage("User information updated successfully!");
      setEditingUser(null);
      setCommunityData({ residencyId: "", blockId: "", flatId: "" });
      setEditData({ mobileNumber: "" });
      setIsEditing(false); // Re-enable useEffect hooks
      await loadUsers(); // Refresh user list
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setError(error.message || "Failed to update user information");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (
      !window.confirm(
        `Are you sure you want to delete user "${
          user.displayName || user.email
        }"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await ApiService.deleteUser(user.id);
      setMessage("User deleted successfully!");
      await loadUsers(); // Refresh user list
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setError(error.message || "Failed to delete user");
    } finally {
      setLoading(false);
    }
  };
  const handleCancelEdit = () => {
    setEditingUser(null);
    setCommunityData({ residencyId: "", blockId: "", flatId: "" });
    setEditData({ mobileNumber: "" });
    setError("");
    setMessage("");
    setIsEditing(false); // Re-enable useEffect hooks
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.carNumber?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesResidency =
      !filterResidency || user.residencyId === parseInt(filterResidency);

    return matchesSearch && matchesResidency;
  });
  const getResidencyName = (residencyId, user) => {
    // First try to get from the user object (new API structure)
    if (user?.residency?.name) {
      return user.residency.name;
    }
    // Fallback to finding in residencies array (old structure)
    const residency = residencies.find((r) => r.id === residencyId);
    return residency?.name || "Not assigned";
  };

  const getBlockName = (blockId, user) => {
    // First try to get from the user object (new API structure)
    if (user?.block?.name) {
      return user.block.name;
    }
    // Fallback to finding in blocks array (old structure)
    const block = blocks.find((b) => b.id === blockId);
    return block?.name || "Not assigned";
  };

  const getFlatNumber = (flatId, user) => {
    // First try to get from the user object (new API structure)
    if (user?.flat?.name) {
      return user.flat.name;
    }
    // Fallback to finding in flats array (old structure)
    const flat = flats.find((f) => f.id === flatId);
    return flat?.number || flat?.name || "Not assigned";
  };
  return (
    <div className="user-management dark-theme">
      <div className="user-management-header">
        <h1>User Management</h1>
        <p>Manage user community assignments</p>
      </div>
      {error && <div className="error-message">{error}</div>}
      {message && <div className="success-message">{message}</div>}
      {/* Filters */}
      <div className="user-filters">
        <div className="filter-group">
          <label>Search Users</label>
          <input
            type="text"
            placeholder="Search by name, email, or car number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <label>Filter by Residency</label>
          <select
            value={filterResidency}
            onChange={(e) => setFilterResidency(e.target.value)}
            className="filter-select"
          >
            <option value="">All Residencies</option>
            {residencies.map((residency) => (
              <option key={residency.id} value={residency.id}>
                {residency.name}
              </option>
            ))}
          </select>
        </div>
      </div>{" "}
      {/* User List */}
      <div className="users-container">
        {loading ? (
          <div className="loading">Loading users...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="no-users">No users found</div>
        ) : (
          <div className="users-table-wrapper">
            {" "}
            <table className="users-table">
              <thead>
                <tr>
                  <th>User Details</th>
                  <th>Car Information</th>
                  <th>Community Assignment</th>
                  <th>Active Subscriptions</th>
                  <th>Actions</th>
                </tr>
              </thead>{" "}
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td data-label="User Details">
                      {" "}
                      <div className="user-details">
                        <div className="user-name">
                          {user.displayName || "No Name"}
                        </div>
                        <div className="user-email">{user.email}</div>
                        <div className="user-mobile">
                          {user.mobileNumber || "No mobile"}
                        </div>
                      </div>
                    </td>
                    <td data-label="Car Information">
                      <div className="car-info">
                        <div className="car-number">
                          {user.carNumber || "Not provided"}
                        </div>
                        <div className="car-model">
                          {user.carModel || "Not provided"}
                        </div>
                      </div>
                    </td>
                    <td data-label="Community Assignment">
                      {" "}
                      <div className="community-info">
                        <div className="community-item">
                          <span className="label">Residency:</span>
                          <span className="value">
                            {getResidencyName(user.residencyId, user)}
                          </span>
                        </div>
                        <div className="community-item">
                          <span className="label">Block:</span>
                          <span className="value">
                            {getBlockName(user.blockId, user)}
                          </span>
                        </div>
                        <div className="community-item">
                          <span className="label">Flat:</span>
                          <span className="value">
                            {getFlatNumber(user.flatId, user)}
                          </span>{" "}
                        </div>
                      </div>
                    </td>
                    <td data-label="Active Subscriptions">
                      <div className="subscription-info">
                        {user.activeSubscriptions &&
                        user.activeSubscriptions.length > 0 ? (
                          user.activeSubscriptions.map(
                            (subscription, index) => (
                              <div
                                key={subscription.id}
                                className="subscription-item"
                              >
                                <div className="subscription-plan">
                                  <span className="plan-name">
                                    {subscription.pricingPlan?.name ||
                                      "Unknown Plan"}
                                  </span>
                                  <span className="car-type">
                                    (
                                    {subscription.carType?.name ||
                                      "Unknown Car Type"}
                                    )
                                  </span>
                                </div>
                                <div className="subscription-validity">
                                  {subscription.endDate ? (
                                    <span
                                      className={`validity ${
                                        new Date(subscription.endDate) <
                                        new Date()
                                          ? "expired"
                                          : "active"
                                      }`}
                                    >
                                      Valid until:{" "}
                                      {new Date(
                                        subscription.endDate
                                      ).toLocaleDateString()}
                                    </span>
                                  ) : (
                                    <span className="validity infinite">
                                      Lifetime validity
                                    </span>
                                  )}
                                </div>
                                {index <
                                  user.activeSubscriptions.length - 1 && (
                                  <hr className="subscription-divider" />
                                )}
                              </div>
                            )
                          )
                        ) : (
                          <div className="no-subscriptions">
                            <span>No active subscriptions</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td data-label="Actions">
                      <div className="action-buttons">
                        {" "}
                        <button
                          onClick={() => handleEditUser(user)}
                          className="edit-btn"
                          disabled={editingUser && editingUser.id !== user.id}
                        >
                          {editingUser && editingUser.id === user.id
                            ? "Editing..."
                            : "Edit"}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="delete-btn"
                          disabled={editingUser}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>{" "}
      {/* Edit User Modal */}
      {editingUser && (
        <div className="edit-modal-overlay">
          <div className="edit-modal">
            {" "}
            <div className="modal-header">
              <h2>Edit User Information</h2>
              <p>
                User: {editingUser.displayName} ({editingUser.email})
              </p>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Mobile Number</label>
                <input
                  type="tel"
                  name="mobileNumber"
                  value={editData.mobileNumber}
                  onChange={handleEditDataChange}
                  placeholder="e.g., 9876543210"
                  pattern="[6-9][0-9]{9}"
                  maxLength="10"
                />
              </div>

              <div className="form-group">
                <label>Residency</label>
                <select
                  name="residencyId"
                  value={communityData.residencyId}
                  onChange={handleCommunityChange}
                >
                  <option value="">Select Residency</option>
                  {residencies.map((residency) => (
                    <option key={residency.id} value={residency.id}>
                      {residency.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Block</label>
                <select
                  name="blockId"
                  value={communityData.blockId}
                  onChange={handleCommunityChange}
                  disabled={!communityData.residencyId}
                >
                  <option value="">Select Block</option>
                  {blocks.map((block) => (
                    <option key={block.id} value={block.id}>
                      {block.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Flat</label>
                <select
                  name="flatId"
                  value={communityData.flatId}
                  onChange={handleCommunityChange}
                  disabled={!communityData.blockId}
                >
                  {" "}
                  <option value="">Select Flat</option>
                  {flats.map((flat) => (
                    <option key={flat.id} value={flat.id}>
                      {flat.name || flat.number}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button
                onClick={handleSaveCommunity}
                disabled={loading}
                className="save-btn"
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={loading}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
